<script setup lang="ts">
import DeviceMeta from '../DeviceMeta';
import { computed } from 'vue';
import type { PropType } from 'vue';
import type { Value } from '../Default/lib';
import type { MetaProps } from '../DeviceMeta/lib';

const props = defineProps({
  magnet: {
    type: Object as PropType<Value>,
    default: undefined,
  },
  meta: {
    type: Object as PropType<MetaProps>,
    required: true,
  },
});

const isOpen = computed(() => props.magnet ? props.magnet.value : undefined);
</script>

<template>
  <div class="w-full text-white font-light font-sans relative">
    <div
      class="flex flex-col items-center justify-center w-full lg:h-30 lg:w-full md:h-26"
      :class="isOpen && 'error'"
    >
      <div
        :class="isOpen ? 'magnet-open-animation' : 'magnet-close-animation'"
        class="flex items-center justify-center h-auto w-[80%] border-1 border-solid border-gray-800 rounded-2xl pt-3"
      >
        <div class="flex items-center justify-center h-auto w-[90%] border-1 border-solid border-cyan-700 rounded-2xl pt-3 border-b-0 animation-2">
          <div class="flex items-center justify-center h-auto w-[90%] border-1 border-solid border-cyan-400 rounded-2xl pt-3 border-b-0 animation-1">
            <div class="h-14 w-[80%] border-1 border-solid border-gray-100 rounded-2xl border-b-3 -mb-[3px]" />
          </div>
        </div>
      </div>
      <div class="flex items-center justify-center h-auto w-[80%] border-1 border-solid border-gray-800 rounded-2xl pb-3 border-t-0 animation-3 ">
        <div class="flex items-center justify-center h-auto w-[90%] border-1 border-solid border-cyan-700 rounded-2xl pb-3 border-t-0 animation-2">
          <div class="flex items-center justify-center h-auto w-[90%] border-1 border-solid border-cyan-400 rounded-2xl pb-3  animation-1">
            <div class="h-14 w-[80%] border-1 border-solid border-gray-100 rounded-2xl border-t-3 -mt-[3px]" />
          </div>
        </div>
      </div>
    </div>

    <DeviceMeta
      v-if="props.meta"
      v-bind="props.meta"
    />
  </div>
</template>

<style scoped lang="scss">
$fast-speed: 2s;
$slow-speed: 2s;
$slower-speed: 2s;

@mixin pulse-keyframe($name, $color) {
  @keyframes #{$name} {
    0%   { border-color: $color; }
    50%  { border-color: rgba(0, 255, 255, 0); }
    100% { border-color: $color; }
  }
}

@mixin pulse-animation($color, $duration, $name) {
  @include pulse-keyframe($name, $color);

  & {
    animation: $name $duration infinite;
  }
}

@keyframes open {
 0% {margin-bottom: 0px;}
 100% {margin-bottom: 20px;}
}

@keyframes closed {
 0% {margin-bottom: 20px;}
 100% {margin-bottom: 0px;}
}

.animation-1 {
  @include pulse-animation(rgba(34, 211, 238, 1), $fast-speed, a1);

  .error & {
    @include pulse-animation(rgb(238, 34, 61), $fast-speed, erro1);
  }
}

.animation-2 {
  @include pulse-animation(rgba(14, 116, 144, 1), $slow-speed, a2);

  & {
    animation-delay: 0.2s;
  }

  .error & {
    @include pulse-animation(rgb(165, 22, 41), $slow-speed, error2);
  }
}

.animation-3 {
  @include pulse-animation(rgba(34, 41, 55, 1), $slower-speed, a3);

  & {
    animation-delay: 0.4s;
  }

  .error & {
    @include pulse-animation(rgb(65, 12, 19), $slower-speed, error3);
  }
}

.magnet-open-animation {
  & {
    animation-delay: 0.4s;
    animation: open 0.3s both linear, outer $slower-speed infinite;
  }

  @include pulse-keyframe(outer, rgba(34, 41, 55, 1));

  .error & {
    animation: open 0.3s both linear, error-outer $slower-speed infinite;
    @include pulse-keyframe(error-outer, rgb(65, 12, 19));
  }
}

.magnet-close-animation {
  & {
    animation: closed 0.3s both linear, outer $slower-speed infinite;
    animation-delay: 0.4s;
  }

  @include pulse-keyframe(outer, rgba(34, 41, 55, 1));

  .error & {
    animation: open 0.3s both linear, error-outer $slower-speed infinite;
    @include pulse-keyframe(error-outer, rgb(65, 12, 19));
  }
}
</style>
